import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { attachNameSuggest, fetchPlayerSuggestions } from '../components/NameSuggest.js';
import type { FriendEntryDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

type Tab = 'friends' | 'blocked' | 'enemies';

interface State {
  tab: Tab;
  friends: FriendEntryDto[];
  blocks: FriendEntryDto[];
  enemies: FriendEntryDto[];
  flash: FlashState | null;
}

export async function renderFriends(root: HTMLElement): Promise<void> {
  const state: State = {
    tab: 'friends',
    friends: [],
    blocks: [],
    enemies: [],
    flash: null,
  };

  async function refresh(): Promise<void> {
    try {
      const [fr, bl, en] = await Promise.all([
        api.listFriends(),
        api.listBlocks(),
        api.listEnemies(),
      ]);
      state.friends = fr.friends;
      state.blocks = bl.blocks;
      state.enemies = en.enemies;
    } catch (err) {
      state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load.' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const tab = state.tab;
      const list = tab === 'friends' ? state.friends : tab === 'blocked' ? state.blocks : state.enemies;
      const addLabel = tab === 'friends' ? '✚ Add friend' : tab === 'blocked' ? '⊘ Block' : '⚑ Mark enemy';
      const emptyMsg =
        tab === 'friends'
          ? 'No friends yet — track who you trust.'
          : tab === 'blocked'
            ? 'No-one blocked.'
            : 'No enemies marked — yet. Mark rivals you intend to hunt.';

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/friends.webp" alt="" />
        <h1>Acquaintances</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          The names you keep close, and the names you keep at arm's length.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <div class="friend-tabs">
            <button class="friend-tab ${tab === 'friends' ? 'active' : ''}" data-tab="friends">
              Friends <span class="pill">${state.friends.length}</span>
            </button>
            <button class="friend-tab ${tab === 'enemies' ? 'active' : ''}" data-tab="enemies">
              Enemies <span class="pill">${state.enemies.length}</span>
            </button>
            <button class="friend-tab ${tab === 'blocked' ? 'active' : ''}" data-tab="blocked">
              Blocked <span class="pill">${state.blocks.length}</span>
            </button>
          </div>

          <div class="friend-add">
            <input type="text" id="friend-handle" placeholder="Enter a player's name…" maxlength="64" />
            <button class="btn" id="friend-add-btn">${addLabel}</button>
          </div>

          <div class="friend-list">
            ${
              list.length === 0
                ? `<p class="friend-empty">${emptyMsg}</p>`
                : list.map((e) => renderRow(e, tab)).join('')
            }
          </div>
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = STYLES;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (state.flash) {
        const div = document.createElement('div');
        div.className = `alert ${state.flash.kind === 'warn' ? 'danger' : state.flash.kind}`;
        div.textContent = state.flash.message;
        flashRoot.appendChild(div);
      }

      // Tab toggle.
      main.querySelectorAll<HTMLButtonElement>('button.friend-tab').forEach((btn) => {
        btn.addEventListener('click', () => {
          const tab = btn.dataset.tab as Tab;
          if (tab === state.tab) return;
          state.tab = tab;
          anchored(renderMain);
        });
      });

      // Add / Block.
      const addBtn = main.querySelector<HTMLButtonElement>('#friend-add-btn');
      const handleInput = main.querySelector<HTMLInputElement>('#friend-handle');
      const doAdd = async (): Promise<void> => {
        const handle = handleInput?.value.trim();
        if (!handle) {
          const verb = tab === 'friends' ? 'add' : tab === 'blocked' ? 'block' : 'mark';
          state.flash = { kind: 'warn', message: `Enter a player's name to ${verb}.` };
          anchored(renderMain);
          return;
        }
        if (addBtn) addBtn.disabled = true;
        try {
          if (tab === 'friends') {
            const r = await api.addFriend({ handle });
            state.flash = { kind: 'success', message: `Added ${r.friend.name} to friends.` };
          } else if (tab === 'blocked') {
            const r = await api.addBlock({ handle });
            state.flash = { kind: 'success', message: `Blocked ${r.block.name}.` };
          } else {
            const r = await api.addEnemy({ handle });
            state.flash = { kind: 'success', message: `Marked ${r.enemy.name} as an enemy.` };
          }
          await refresh();
        } catch (err) {
          state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed.' };
        } finally {
          anchored(renderMain);
        }
      };
      addBtn?.addEventListener('click', doAdd);
      // Typeahead first (it swallows Enter when a suggestion is highlighted),
      // then the plain Enter-to-add behaviour.
      if (handleInput) attachNameSuggest(handleInput, fetchPlayerSuggestions);
      handleInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void doAdd();
      });

      // Remove buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-remove]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.remove!;
          const name = btn.dataset.name!;
          const tab = state.tab;
          const dialog =
            tab === 'friends'
              ? { title: 'Remove friend?', message: `${name} will be removed from your friends list.`, confirmLabel: 'Remove' }
              : tab === 'blocked'
                ? { title: 'Unblock player?', message: `${name} will be unblocked. They'll be able to mail you and send you crowns again.`, confirmLabel: 'Unblock' }
                : { title: 'Remove enemy?', message: `${name} will be removed from your enemies list.`, confirmLabel: 'Remove' };
          const ok = await showConfirmDialog(dialog);
          if (!ok) return;
          try {
            if (tab === 'friends') await api.removeFriend(id);
            else if (tab === 'blocked') await api.removeBlock(id);
            else await api.removeEnemy(id);
            state.flash = {
              kind: 'success',
              message: tab === 'friends' ? 'Friend removed.' : tab === 'blocked' ? 'Player unblocked.' : 'Enemy removed.',
            };
            await refresh();
          } catch (err) {
            state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Block-from-row (one-click block a friend or enemy).
      main.querySelectorAll<HTMLButtonElement>('button[data-block-name]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const name = btn.dataset.blockName!;
          const ok = await showConfirmDialog({
            title: 'Block this player?',
            message: `${name} will be added to your blocked list. Neither of you will be able to mail or send crowns to the other (they can still attack you).`,
            confirmLabel: 'Block',
          });
          if (!ok) return;
          try {
            await api.addBlock({ handle: name });
            state.flash = { kind: 'success', message: `Blocked ${name}.` };
            await refresh();
          } catch (err) {
            state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });
    };

    anchored(renderMain);
  });
}

function renderRow(e: FriendEntryDto, tab: Tab): string {
  const nameLink = `<a href="#/u/${encodeURIComponent(e.characterId)}">${escapeHtml(e.name)}</a>`;
  const mutual = e.mutual && tab === 'friends' ? '<span class="mutual-pip" title="Mutual">⚭</span>' : '';
  // Blocked rows: Unblock only. Friends + Enemies both get Block + Remove.
  const actions =
    tab === 'blocked'
      ? `<button class="btn ghost" data-remove="${e.id}" data-name="${escapeAttr(e.name)}">Unblock</button>`
      : `<button class="btn ghost" data-block-name="${escapeAttr(e.name)}" title="Block this player">Block</button>
         <button class="btn ghost" data-remove="${e.id}" data-name="${escapeAttr(e.name)}">Remove</button>`;
  return `
    <div class="friend-row">
      <div class="friend-info">
        <div class="friend-name">${nameLink} ${mutual}</div>
        <div class="friend-meta">Lvl ${e.level} · ${prettyStatus(e.status)} · added ${formatRelative(e.addedAt)}</div>
      </div>
      <div class="friend-actions">${actions}</div>
    </div>
  `;
}

function prettyStatus(s: string): string {
  if (s === 'OKAY') return 'Hale';
  if (s === 'INFIRMARY') return 'Infirmary';
  if (s === 'DUNGEON') return 'Dungeon';
  if (s === 'TRAVELLING') return 'Travelling';
  return s;
}

function formatRelative(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function escapeAttr(s: string): string { return escapeHtml(s); }

const STYLES = `
  .friend-tabs {
    display: flex;
    gap: var(--s-2);
    margin-bottom: var(--s-3);
  }
  .friend-tab {
    flex: 0 0 auto;
    font-family: var(--font-display);
    font-size: 1rem;
    letter-spacing: 0.04em;
    padding: 8px 16px;
    background: rgba(255, 245, 220, 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    /* The cell stays light-cream in both themes, so pin dark ink (the soft-ink
       token flips to cream in dark mode and washed out on this cell). */
    color: rgba(28, 17, 8, 0.75);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .friend-tab.active {
    background: var(--c-oxblood);
    color: #fff5e1;
    border-color: var(--c-oxblood-deep);
  }
  .friend-tab.active .pill { background: rgba(255, 245, 220, 0.18); color: #fff5e1; border-color: rgba(255, 245, 220, 0.3); }
  .friend-add {
    display: flex;
    gap: var(--s-2);
    margin-bottom: var(--s-3);
  }
  .friend-add input {
    flex: 1 1 auto;
    min-width: 0;
    font: inherit;
    font-family: var(--font-body);
    padding: var(--s-2) var(--s-3);
    /* Colours come from the global pinned cream cell in styles/pages.css. */
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    box-shadow: var(--shadow-press);
  }
  .friend-add input:focus {
    outline: none;
    border-color: var(--c-gold);
    box-shadow: var(--shadow-press), 0 0 0 3px rgba(212, 169, 60, 0.25);
  }
  .friend-list { display: flex; flex-direction: column; }
  .friend-empty {
    font-family: var(--font-elegant);
    font-style: italic;
    text-align: center;
    color: var(--c-ink-soft);
    padding: var(--s-4);
  }
  .friend-row {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    padding: 10px 0;
    border-bottom: 1px solid var(--c-rule-soft);
  }
  .friend-row:last-child { border-bottom: 0; }
  .friend-info { flex: 1 1 auto; min-width: 0; }
  .friend-name {
    font-family: var(--font-display);
    font-size: 1.05rem;
    color: var(--c-ink);
    letter-spacing: 0.03em;
  }
  .mutual-pip {
    color: var(--c-gold-text);
    font-size: 1.15rem;
    font-weight: 700;
    margin-left: 4px;
  }
  .friend-meta {
    font-family: var(--font-display);
    font-size: 0.92rem;
    color: var(--c-ink-soft);
    margin-top: 2px;
  }
  .friend-actions {
    display: flex;
    gap: var(--s-2);
  }
`;
